import React, { useEffect, useState, Component } from 'react';
import { CheckCircle2, XCircle, Star, ShieldCheck, Clock, BookOpen, ChevronRight, Download, CreditCard, Lock, ArrowRight, AlertCircle } from 'lucide-react';
import { BonusSection } from './components/BonusSection';
import { PricingSelector, goToCheckout } from './components/PricingSelector';
// --- Components ---
const PurchasePopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [purchaseData, setPurchaseData] = useState({
    name: '',
    city: ''
  });
  const names = ['María', 'Juan', 'Laura', 'Carlos', 'Ana', 'Diego', 'Sofía', 'Martín', 'Lucía', 'Pablo'];
  const cities = ['Buenos Aires', 'Córdoba', 'Rosario', 'Mendoza', 'Tucumán', 'La Plata', 'Mar del Plata', 'Salta', 'Santa Fe', 'San Juan'];
  useEffect(() => {
    let hideTimer: ReturnType<typeof setTimeout>;
    const showPopup = () => {
      const randomName = names[Math.floor(Math.random() * names.length)];
      const randomCity = cities[Math.floor(Math.random() * cities.length)];
      setPurchaseData({
        name: randomName,
        city: randomCity
      });
      setIsVisible(true);
      hideTimer = setTimeout(() => {
        setIsVisible(false);
      }, 5000);
    };
    // Primer aviso a los 10s, luego cada 30 segundos
    const initialTimer = setTimeout(showPopup, 10000);
    const interval = setInterval(showPopup, 30000);
    return () => {
      clearTimeout(initialTimer);
      clearTimeout(hideTimer);
      clearInterval(interval);
    };
  }, []);
  // Lightweight CSS transition (no framer-motion) — keeps the bundle small
  return <div aria-hidden={!isVisible} className={`fixed bottom-4 left-4 md:bottom-8 md:left-8 bg-white rounded-lg shadow-xl border border-gray-200 p-4 flex items-center gap-4 z-50 max-w-sm transition-all duration-300 ${isVisible ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-6 pointer-events-none'}`} data-id="element-0">
      <div className="bg-green-100 p-2 rounded-full text-green-600" data-id="element-1">
        <CheckCircle2 size={24} data-id="element-2" />
      </div>
      <div data-id="element-3">
        <p className="text-sm text-gray-600" data-id="element-4">Alguien acaba de comprar</p>
        <p className="font-bold text-gray-900" data-id="element-5">
          {purchaseData.name} de {purchaseData.city}
        </p>
        <p className="text-xs text-gray-500 mt-1" data-id="element-6">hace unos minutos</p>
      </div>
    </div>;
};
const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 59,
    seconds: 37
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return {
            ...prev,
            seconds: prev.seconds - 1
          };
        } else if (prev.minutes > 0) {
          return {
            ...prev,
            minutes: prev.minutes - 1,
            seconds: 59
          };
        } else if (prev.hours > 0) {
          return {
            hours: prev.hours - 1,
            minutes: 59,
            seconds: 59
          };
        }
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  return <div className="flex justify-center gap-4 text-center my-6" data-id="element-7">
      <div className="bg-red-600 text-white rounded-lg p-3 w-20" data-id="element-8">
        <div className="text-2xl font-bold" data-id="element-9">
          {String(timeLeft.hours).padStart(2, '0')}
        </div>
        <div className="text-xs uppercase" data-id="element-10">Horas</div>
      </div>
      <div className="text-red-600 text-2xl font-bold self-center" data-id="element-11">:</div>
      <div className="bg-red-600 text-white rounded-lg p-3 w-20" data-id="element-12">
        <div className="text-2xl font-bold" data-id="element-13">
          {String(timeLeft.minutes).padStart(2, '0')}
        </div>
        <div className="text-xs uppercase" data-id="element-14">Min</div>
      </div>
      <div className="text-red-600 text-2xl font-bold self-center" data-id="element-15">:</div>
      <div className="bg-red-600 text-white rounded-lg p-3 w-20" data-id="element-16">
        <div className="text-2xl font-bold" data-id="element-17">
          {String(timeLeft.seconds).padStart(2, '0')}
        </div>
        <div className="text-xs uppercase" data-id="element-18">Seg</div>
      </div>
    </div>;
};
export function App() {
  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing');
    if (pricingSection) {
      pricingSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-gray-50 font-sans text-gray-800" data-id="element-19">
      <PurchasePopup data-id="element-20" />

      {/* 🔴 SECCIÓN 1 — HERO */}
      <header className="bg-white border-b-4 border-blue-600 px-4" data-id="element-21">
        {/* Above-the-fold: entra en una pantalla de iPhone (inicio → primer CTA) sin scroll */}
        <div className="max-w-4xl mx-auto text-center flex flex-col justify-center min-h-[100svh] md:min-h-0 py-4 md:py-16" data-id="element-22">
          <p className="text-blue-600 font-bold tracking-wider uppercase mb-2 md:mb-4 text-xs sm:text-sm md:text-base" data-id="element-23">
            PROFE LISTO · BIOLOGÍA 360
          </p>
          <h1 className="text-2xl sm:text-3xl md:text-6xl font-extrabold text-gray-900 mb-3 md:mb-6 leading-tight" data-id="element-24">
            Todo tu año de Biología <br className="hidden md:block" data-id="element-25" />
            <span className="text-blue-600" data-id="element-26">resuelto de una vez.</span>
          </h1>

          {/* Main Product Image */}
          <div className="mb-3 md:mb-8 mx-auto max-w-xl" data-id="element-27">
            <img src="https://cdn.magicpatterns.com/uploads/s9iyDUiLdqqG1B2TDhwgfF/biologia_mock.png" alt="Megapack Biología 360 — Plan anual completo y recursos élite" width={1080} height={1350} loading="eager" fetchPriority="high" decoding="async" className="mx-auto w-auto h-auto max-h-[34vh] md:max-h-none md:w-full object-contain" data-id="element-28" />
          </div>

          <p className="text-base sm:text-lg md:text-2xl text-gray-600 mb-2 md:mb-8 max-w-2xl mx-auto font-medium" data-id="element-29">
            Megapack BIOLOGÍA 360 — Pack Anual Elite Completo
          </p>
          <p className="hidden md:block text-lg text-gray-600 mb-8 max-w-2xl mx-auto" data-id="element-30">
            El sistema completo de planificación y materiales listos para usar
            para el profesor de Biología argentino.
          </p>

          <div className="flex flex-wrap justify-center gap-2 md:gap-4 text-xs md:text-sm font-medium text-gray-700 mb-4 md:mb-10" data-id="element-31">
            <span className="flex items-center gap-1 bg-gray-100 px-2.5 md:px-3 py-1 md:py-1.5 rounded-full" data-id="element-32">
              <Download size={14} className="text-blue-600" data-id="element-33" /> 📄 PDF
            </span>
            <span className="flex items-center gap-1 bg-gray-100 px-2.5 md:px-3 py-1 md:py-1.5 rounded-full" data-id="element-34">
              <Clock size={14} className="text-blue-600" data-id="element-35" /> ⚡ Acceso Inmediato
            </span>
            <span className="flex items-center gap-1 bg-gray-100 px-2.5 md:px-3 py-1 md:py-1.5 rounded-full" data-id="element-36">
              <BookOpen size={14} className="text-blue-600" data-id="element-37" /> 📚 12 Tomos + 6
              Bonus + 2 Élite
            </span>
          </div>

          <button onClick={scrollToPricing} className="w-full md:w-auto bg-green-500 hover:bg-green-600 text-white text-lg md:text-xl font-bold py-3.5 md:py-4 px-8 rounded-full shadow-[0_4px_0_rgb(21,128,61)] hover:shadow-[0_2px_0_rgb(21,128,61)] hover:translate-y-[2px] transition-all flex items-center justify-center gap-2 mx-auto" data-id="element-38">
            💪 QUIERO MI BIOLOGÍA 360 AHORA
          </button>
        </div>

        {/* Below-the-fold: confianza + stats */}
        <div className="max-w-4xl mx-auto text-center pb-12 md:pb-0" data-id="element-39">
          <div className="flex flex-wrap justify-center gap-4 md:gap-8 text-sm text-gray-500 mt-6" data-id="element-40">
            <span className="flex items-center gap-1" data-id="element-41">
              <ShieldCheck size={16} data-id="element-42" /> 🛡️ Garantía 7 días
            </span>
            <span className="hidden md:inline text-gray-300" data-id="element-43">|</span>
            <span className="flex items-center gap-1" data-id="element-44">
              <Lock size={16} data-id="element-45" /> 🔒 Pago seguro
            </span>
            <span className="hidden md:inline text-gray-300" data-id="element-46">|</span>
            <span className="flex items-center gap-1" data-id="element-47">
              <CreditCard size={16} data-id="element-48" /> 💳 Tarjetas y transferencia
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12 border-t border-gray-200 pt-8" data-id="element-49">
            <div data-id="element-50">
              <p className="text-2xl font-bold text-gray-900" data-id="element-51">+500</p>
              <p className="text-sm text-gray-500" data-id="element-52">profes confían</p>
            </div>
            <div data-id="element-53">
              <p className="text-2xl font-bold text-gray-900" data-id="element-54">12+6</p>
              <p className="text-sm text-gray-500" data-id="element-55">Tomos + Bonus</p>
            </div>
            <div data-id="element-56">
              <p className="text-2xl font-bold text-gray-900 flex items-center justify-center gap-1" data-id="element-57">
                4.9/5{' '}
                <Star size={16} className="fill-yellow-400 text-yellow-400" data-id="element-58" />
              </p>
              <p className="text-sm text-gray-500" data-id="element-59">calificación</p>
            </div>
            <div data-id="element-60">
              <p className="text-2xl font-bold text-gray-900" data-id="element-61">🇦🇷</p>
              <p className="text-sm text-gray-500" data-id="element-62">Argentina</p>
            </div>
          </div>
        </div>
      </header>

      {/* 🔴 SECCIÓN 2 — TESTIMONIOS APERTURA */}
      <section className="py-16 bg-gray-50 px-4" data-id="element-63">
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6" data-id="element-64">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-65">
            <div className="flex text-yellow-400 mb-3" data-id="element-66">
              {[...Array(5)].map((_, i) => <Star key={i} size={16} className="fill-current" data-id="element-67" />)}
            </div>
            <p className="text-gray-700 italic mb-4" data-id="element-68">
              "Organicé todo el mes en una tarde. Nunca más llego al aula sin
              saber qué dar."
            </p>
            <p className="font-bold text-sm text-gray-900" data-id="element-69">
              — María P., Buenos Aires
            </p>
            <p className="text-xs text-gray-500" data-id="element-70">
              Profesora de Biología en Secundaria Pública
            </p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-71">
            <div className="flex text-yellow-400 mb-3" data-id="element-72">
              {[...Array(5)].map((_, i) => <Star key={i} size={16} className="fill-current" data-id="element-73" />)}
            </div>
            <p className="text-gray-700 italic mb-4" data-id="element-74">
              "Mis alumnos de 1° año mejoraron su comprensión de la célula desde
              la primera semana con el Tomo 1."
            </p>
            <p className="font-bold text-sm text-gray-900" data-id="element-75">
              — Claudia G., Córdoba
            </p>
            <p className="text-xs text-gray-500" data-id="element-76">
              Profesora de Biología en Colegio Privado
            </p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-77">
            <div className="flex text-yellow-400 mb-3" data-id="element-78">
              {[...Array(5)].map((_, i) => <Star key={i} size={16} className="fill-current" data-id="element-79" />)}
            </div>
            <p className="text-gray-700 italic mb-4" data-id="element-80">
              "Antes tardaba 2 horas armando una actividad. Ahora son 10
              minutos."
            </p>
            <p className="font-bold text-sm text-gray-900" data-id="element-81">
              — Federico R., Rosario
            </p>
            <p className="text-xs text-gray-500" data-id="element-82">
              Profesor de Biología — 4 escuelas simultáneas
            </p>
          </div>
        </div>
      </section>

      {/* 🔴 SECCIÓN 3 — DOLORES DEL BUYER PERSONA */}
      <section className="py-16 bg-white px-4" data-id="element-83">
        <div className="max-w-3xl mx-auto" data-id="element-84">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-10" data-id="element-85">
            Si sos profe de Biología en Argentina, esto te va a sonar conocido:
          </h2>

          <div className="space-y-4 mb-12" data-id="element-86">
            {['Llegás al aula sin saber exactamente qué actividad vas a dar hoy', 'Pasás los domingos buscando materiales en internet que nunca terminan de encajar con el diseño curricular argentino', 'Trabajás en 3 o 4 escuelas a la vez y no tenés tiempo físico para planificar cada curso por separado', 'Tus alumnos no progresan porque no hay una secuencia clara de contenidos entre clase y clase', 'El directivo te pide que integres ESI y Educación Ambiental y no sabés por dónde empezar sin perder horas', 'Cuando hay feriado o falta perdés el hilo y llegás a diciembre sin haber cubierto la mitad del programa', 'Nadie valora tu trabajo porque no se ve reflejado en resultados concretos ni en una planificación visible y sólida', 'Corregís pilas de trabajos prácticos que inventaste a último momento sin guía de corrección ni criterios claros'].map((pain, idx) => <div key={idx} className="flex items-start gap-3 bg-red-50 p-4 rounded-lg" data-id="element-87">
                <XCircle className="text-red-500 shrink-0 mt-0.5" size={20} data-id="element-88" />
                <p className="text-gray-700" data-id="element-89">{pain}</p>
              </div>)}
          </div>

          <div className="text-center bg-gray-900 text-white p-8 rounded-2xl" data-id="element-90">
            <p className="text-2xl font-bold italic mb-6" data-id="element-91">
              "¿Cuántas clases más vas a llegar con las manos vacías?"
            </p>
            <button onClick={scrollToPricing} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-full transition-colors flex items-center justify-center gap-2 mx-auto w-full md:w-auto" data-id="element-92">
              👉 BASTA DE IMPROVISAR — QUIERO MI BIOLOGÍA 360
            </button>
          </div>
        </div>
      </section>

      {/* 🔴 SECCIÓN 4 — PRESENTACIÓN DEL SISTEMA */}
      <section className="py-20 bg-blue-600 text-white px-4" data-id="element-93">
        <div className="max-w-4xl mx-auto text-center" data-id="element-94">
          <div className="inline-block bg-blue-500 px-4 py-1 rounded-full text-sm font-bold tracking-wider mb-6" data-id="element-95">
            🏆 El Sistema N°1 para Profesores de Biología Argentinos
          </div>
          <h2 className="text-3xl md:text-5xl font-extrabold mb-6" data-id="element-96">
            BIOLOGÍA 360 — Colección Completa
          </h2>
          <p className="text-xl md:text-2xl text-blue-100 mb-10" data-id="element-97">
            12 Tomos + 6 Bonus + 2 Élite — Todo resuelto, todos los niveles.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center" data-id="element-98">
            <div className="bg-blue-700/50 p-4 rounded-xl" data-id="element-99">
              <p className="text-3xl font-bold mb-1" data-id="element-100">+280</p>
              <p className="text-sm text-blue-200" data-id="element-101">páginas</p>
            </div>
            <div className="bg-blue-700/50 p-4 rounded-xl" data-id="element-102">
              <p className="text-3xl font-bold mb-1" data-id="element-103">+240</p>
              <p className="text-sm text-blue-200" data-id="element-104">actividades listas</p>
            </div>
            <div className="bg-blue-700/50 p-4 rounded-xl" data-id="element-105">
              <p className="text-3xl font-bold mb-1" data-id="element-106">1° a 6°</p>
              <p className="text-sm text-blue-200" data-id="element-107">año completo</p>
            </div>
            <div className="bg-blue-700/50 p-4 rounded-xl" data-id="element-108">
              <p className="text-3xl font-bold mb-1" data-id="element-109">🇦🇷</p>
              <p className="text-sm text-blue-200" data-id="element-110">
                Alineado al diseño curricular
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 🔴 SECCIÓN 5 — LOS 12 TOMOS */}
      <section className="py-20 bg-gray-50 px-4" data-id="element-111">
        <div className="max-w-5xl mx-auto" data-id="element-112">
          <div className="text-center mb-16" data-id="element-113">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-114">
              Los 12 Tomos Completos
            </h2>
            <p className="text-xl text-gray-600" data-id="element-115">
              Actividades listas · Por año y cuatrimestre · Listo para imprimir
              mañana
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-116">
            {[{
            num: 1,
            year: '1° Año',
            term: 'Primer Cuatrimestre',
            title: 'Los seres vivos y su diversidad',
            desc: 'La célula, los reinos, las relaciones ecológicas básicas. El punto de partida de toda la biología.'
          }, {
            num: 2,
            year: '1° Año',
            term: 'Segundo Cuatrimestre',
            title: 'El cuerpo humano y los sistemas de vida',
            desc: 'Todos los sistemas del cuerpo humano. Actividades listas para cada clase.'
          }, {
            num: 3,
            year: '2° Año',
            term: 'Primer Cuatrimestre',
            title: 'Evolución, genética básica y origen de la vida',
            desc: 'Darwin, Lamarck, ADN y herencia mendeliana explicados para adolescentes argentinos.'
          }, {
            num: 4,
            year: '2° Año',
            term: 'Segundo Cuatrimestre',
            title: 'Ecología, ecosistemas y problemáticas ambientales',
            desc: 'Ciclos biogeoquímicos, biomas argentinos y crisis ambiental con datos locales.'
          }, {
            num: 5,
            year: '3° Año',
            term: 'Primer Cuatrimestre',
            title: 'Biología celular avanzada',
            desc: 'Mitosis, meiosis, respiración celular y fotosíntesis con actividades de laboratorio.'
          }, {
            num: 6,
            year: '3° Año',
            term: 'Segundo Cuatrimestre',
            title: 'Histología, tejidos y microbiología',
            desc: 'Tipos de tejidos, microorganismos y enfermedades infecciosas actuales.'
          }, {
            num: 7,
            year: '4° Año',
            term: 'Primer Cuatrimestre',
            title: 'Genética molecular',
            desc: 'ADN, ARN, síntesis de proteínas y expresión génica con casos reales.'
          }, {
            num: 8,
            year: '4° Año',
            term: 'Segundo Cuatrimestre',
            title: 'Biotecnología y bioética',
            desc: 'CRISPR, OGM y bioética aplicada. El debate científico más actual en el aula.'
          }, {
            num: 9,
            year: '5° Año',
            term: 'Primer Cuatrimestre',
            title: 'Fisiología humana y homeostasis',
            desc: 'Regulación del cuerpo humano a nivel sistémico. Casos clínicos y actividades de análisis.'
          }, {
            num: 10,
            year: '5° Año',
            term: 'Segundo Cuatrimestre',
            title: 'Neurobiología y conducta',
            desc: 'El sistema nervioso, hormonas y conducta humana para alumnos de 5° año.'
          }, {
            num: 11,
            year: '6° Año',
            term: 'Primer Cuatrimestre',
            title: 'Biología molecular avanzada y sistemática',
            desc: 'Teoría sintética, Hardy-Weinberg, filogenia y evolución humana.'
          }, {
            num: 12,
            year: '6° Año',
            term: 'Segundo Cuatrimestre',
            title: 'Ecología avanzada, crisis ambiental y ciencia ciudadana',
            desc: 'El cierre del ciclo secundario con proyectos integradores y ética ambiental.'
          }].map(tomo => <div key={tomo.num} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow" data-id="element-117">
                <div className="bg-blue-600 text-white p-4" data-id="element-118">
                  <div className="flex justify-between items-center mb-2" data-id="element-119">
                    <span className="font-bold text-lg" data-id="element-120">TOMO {tomo.num}</span>
                    <span className="text-xs bg-blue-500 px-2 py-1 rounded" data-id="element-121">
                      {tomo.year}
                    </span>
                  </div>
                  <p className="text-blue-100 text-sm" data-id="element-122">{tomo.term}</p>
                </div>
                <div className="p-5" data-id="element-123">
                  <h3 className="font-bold text-gray-900 mb-2" data-id="element-124">
                    "{tomo.title}"
                  </h3>
                  <p className="text-gray-600 text-sm" data-id="element-125">{tomo.desc}</p>
                </div>
              </div>)}
          </div>

          <div className="mt-12 bg-white rounded-xl p-6 border border-gray-200 flex flex-wrap justify-center gap-6 text-sm font-medium text-gray-700" data-id="element-126">
            <span className="flex items-center gap-2" data-id="element-127">
              <span className="text-xl" data-id="element-128">✏️</span> Actividades listas en cada
              tomo
            </span>
            <span className="flex items-center gap-2" data-id="element-129">
              <span className="text-xl" data-id="element-130">⚡</span> Sistema "Imprimís y Das Clase"
            </span>
            <span className="flex items-center gap-2" data-id="element-131">
              <span className="text-xl" data-id="element-132">📋</span> Fichas A4 para fotocopiadora
            </span>
            <span className="flex items-center gap-2" data-id="element-133">
              <span className="text-xl" data-id="element-134">🇦🇷</span> Contenido 100% adaptado a
              Argentina
            </span>
          </div>
        </div>
      </section>

      {/* 🔴 SECCIÓN 6 — LOS 6 BONUS */}
      <BonusSection data-id="element-135" />

      {/* CTA Intermedio */}
      <section className="py-12 bg-blue-50 px-4" data-id="element-136">
        <div className="max-w-3xl mx-auto text-center" data-id="element-137">
          <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-138">
            Asegurá todos estos bonos gratis hoy mismo
          </h3>
          <button onClick={scrollToPricing} className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white text-lg font-bold py-4 px-8 rounded-full shadow-[0_4px_0_rgb(29,78,216)] hover:shadow-[0_2px_0_rgb(29,78,216)] hover:translate-y-[2px] transition-all flex items-center justify-center gap-2 mx-auto" data-id="element-139">
            VER OPCIONES DE COMPRA <ArrowRight size={20} data-id="element-140" />
          </button>
        </div>
      </section>

      {/* 🔴 SECCIÓN 7 — PRECIO Y CTA PRINCIPAL */}
      <section id="pricing" className="py-20 bg-white px-4" data-id="element-141">
        <div className="max-w-5xl mx-auto" data-id="element-142">
          <div className="text-center mb-12" data-id="element-143">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-144">
              ⏰ PRECIO DE LANZAMIENTO — OFERTA POR TIEMPO LIMITADO
            </h2>
            <p className="text-red-600 font-bold flex items-center justify-center gap-2 mb-6" data-id="element-145">
              <AlertCircle size={20} data-id="element-146" /> El precio sube cuando termina el
              contador
            </p>
            <CountdownTimer data-id="element-147" />
          </div>

          <PricingSelector data-id="element-148" />
        </div>
      </section>

      {/* 🔴 SECCIÓN 8 — ORDER BUMP / ¿QUÉ AGREGA EL MEGAPACK? */}
      <section className="py-16 bg-blue-900 text-white px-4" data-id="element-149">
        <div className="max-w-4xl mx-auto" data-id="element-150">
          <div className="text-center mb-12" data-id="element-151">
            <h2 className="text-3xl font-bold mb-4" data-id="element-152">
              ¿Qué agrega el Megapack Élite?
            </h2>
            <p className="text-xl text-blue-200" data-id="element-153">
              Por solo $9.000 más, tenés el año 100% resuelto: adentro y afuera
              del aula.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-154">
            <div className="bg-blue-800 p-8 rounded-2xl border border-blue-700" data-id="element-155">
              <div className="text-4xl mb-4" data-id="element-156">🔬</div>
              <h3 className="text-xl font-bold mb-4 text-blue-100" data-id="element-157">
                Élite 1 — El Banco Pro de Trabajos Prácticos
              </h3>
              <p className="text-blue-200 leading-relaxed" data-id="element-158">
                20 TPs listos para fotocopiar desde 1° hasta 6° año. Laboratorio
                casero, comprensión lectora científica y observación de campo.
                Con guía de corrección incluida. No inventés más la pólvora cada
                semana.
              </p>
            </div>
            <div className="bg-blue-800 p-8 rounded-2xl border border-blue-700" data-id="element-159">
              <div className="text-4xl mb-4" data-id="element-160">🌿</div>
              <h3 className="text-xl font-bold mb-4 text-blue-100" data-id="element-161">
                Élite 2 — Proyectos Transversales ESI + EAI
              </h3>
              <p className="text-blue-200 leading-relaxed" data-id="element-162">
                El manual que resuelve la integración obligatoria en el PEI sin
                improvisar. 12 proyectos listos (6 ESI + 6 EAI) con normativa
                citada (Ley 26.150, Ley 27.621, Res. CFE 340/18 y 389/22).
                Cuando el directivo te pida la planificación, tenés todo
                resuelto con respaldo legal.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 🔴 SECCIÓN 9 — TABLA DE CONTRASTE */}
      <section className="py-20 bg-white px-4" data-id="element-163">
        <div className="max-w-4xl mx-auto" data-id="element-164">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12" data-id="element-165">
            ¿Cuánto cuesta NO tenerlo?
          </h2>

          <div className="overflow-x-auto" data-id="element-166">
            <table className="w-full text-left border-collapse" data-id="element-167">
              <thead data-id="element-168">
                <tr data-id="element-169">
                  <th className="w-1/2 p-4 bg-red-50 text-red-900 font-bold text-lg border-b-2 border-red-200 rounded-tl-xl" data-id="element-170">
                    SIN BIOLOGÍA 360
                  </th>
                  <th className="w-1/2 p-4 bg-green-50 text-green-900 font-bold text-lg border-b-2 border-green-200 rounded-tr-xl" data-id="element-171">
                    CON BIOLOGÍA 360
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-172">
                {[['Horas buscando materiales cada semana', '10 minutos. Todo listo.'], ['Actividades inventadas sin progresión real', '+240 actividades con secuencia didáctica'], ['Grupos sin estructura clara de contenidos', 'Estructura clara para cada año y cuatrimestre'], ['ESI y EAI sin resolver en tu planificación', '12 proyectos con normativa incluida'], ['TPs armados a las 11pm del domingo', '20 TPs con guía de corrección listos'], ['Semáforo de notas hecho a mano en la calculadora', 'Planilla automática que hace los números sola'], ['Sensación de improvisar siempre', 'Llegás al aula con todo resuelto']].map((row, idx) => <tr key={idx} className="border-b border-gray-100" data-id="element-173">
                    <td className="p-4 text-gray-600 flex items-start gap-2" data-id="element-174">
                      <XCircle className="text-red-400 shrink-0 mt-1" size={16} data-id="element-175" />{' '}
                      {row[0]}
                    </td>
                    <td className="p-4 text-gray-900 font-medium bg-green-50/30" data-id="element-176">
                      <div className="flex items-start gap-2" data-id="element-177">
                        <CheckCircle2 className="text-green-500 shrink-0 mt-1" size={16} data-id="element-178" />{' '}
                        {row[1]}
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          <div className="text-center mt-10" data-id="element-179">
            <p className="text-2xl font-bold text-gray-900 italic" data-id="element-180">
              El lunes podés improvisar o entrar con todo resuelto. Vos decidís.
            </p>
          </div>
        </div>
      </section>

      {/* 🔴 SECCIÓN 10 — TESTIMONIOS AMPLIADOS */}
      <section className="py-20 bg-gray-50 px-4" data-id="element-181">
        <div className="max-w-4xl mx-auto" data-id="element-182">
          <div className="text-center mb-12" data-id="element-183">
            <h2 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-184">
              Lo que dicen los profesores
            </h2>
            <p className="text-xl font-bold text-gray-600 flex items-center justify-center gap-2" data-id="element-185">
              4.9/5{' '}
              <Star className="fill-yellow-400 text-yellow-400" size={24} data-id="element-186" />
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-187">
            {[{
            init: 'MP',
            name: 'María P.',
            loc: 'Buenos Aires 🇦🇷',
            role: 'Profesora de Biología — Escuela Secundaria Pública',
            text: 'Compré un sábado. El domingo a la noche tenía el mes de clases listo. Nunca más llego al aula sin saber qué dar.'
          }, {
            init: 'CG',
            name: 'Claudia G.',
            loc: 'Córdoba 🇦🇷',
            role: 'Profesora de Biología — Colegio Privado',
            text: 'Tres alumnos de 1° año que no entendían la célula mejoraron desde la primera semana con el Tomo 1. Las actividades están pensadas para cómo aprenden los chicos hoy.'
          }, {
            init: 'FR',
            name: 'Federico R.',
            loc: 'Rosario 🇦🇷',
            role: 'Profesor de Biología — 4 escuelas simultáneas',
            text: 'Antes tardaba 2 horas armando una actividad. Ahora son 10 minutos. Trabajo en 4 escuelas y esto me salvó la vida.'
          }, {
            init: 'SV',
            name: 'Sofía V.',
            loc: 'Mendoza 🇦🇷',
            role: 'Profesora de Biología — Secundaria y Terciario',
            text: 'Los Bonus cambiaron cómo manejo las notas. La Planilla Maestra hace todo sola y el semáforo me avisó de 3 alumnos en riesgo antes de que llegaran a diciembre en problemas.'
          }, {
            init: 'DA',
            name: 'Diego A.',
            loc: 'Santa Fe 🇦🇷',
            role: 'Profesor de Biología — Escuela Técnica',
            text: 'El Ebook Elite de TPs me salvó. Tengo el experimento de extracción de ADN listo para hacer sin laboratorio equipado. Mis alumnos lo hicieron con una frutilla.'
          }, {
            init: 'CR',
            name: 'Claudia R.',
            loc: 'Tucumán 🇦🇷',
            role: 'Profesora de Biología — Secundaria Pública',
            text: 'Los Proyectos ESI y EAI me resolvieron lo que más miedo me daba. Entregué la planificación con normativa citada y la directora me felicitó. Nunca había pasado eso antes.'
          }].map((test, idx) => <div key={idx} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200" data-id="element-188">
                <div className="flex items-center gap-4 mb-4" data-id="element-189">
                  <div className="w-12 h-12 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-bold text-lg" data-id="element-190">
                    {test.init}
                  </div>
                  <div data-id="element-191">
                    <div className="flex text-yellow-400 mb-1" data-id="element-192">
                      {[...Array(5)].map((_, i) => <Star key={i} size={14} className="fill-current" data-id="element-193" />)}
                    </div>
                    <p className="font-bold text-gray-900" data-id="element-194">
                      {test.name} — {test.loc}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-195">{test.role}</p>
                  </div>
                </div>
                <p className="text-gray-700 italic" data-id="element-196">"{test.text}"</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 🔴 SECCIÓN 11 — GARANTÍA */}
      <section className="py-16 bg-blue-50 px-4" data-id="element-197">
        <div className="max-w-3xl mx-auto text-center bg-white p-10 rounded-3xl shadow-lg border border-blue-100" data-id="element-198">
          <ShieldCheck size={64} className="mx-auto text-blue-600 mb-6" data-id="element-199" />
          <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-200">
            Garantía 7 días sin preguntas
          </h2>
          <p className="text-lg text-gray-600 mb-6 leading-relaxed" data-id="element-201">
            Abrí los 12 Tomos. Revisá los 6 Bonus. Usá los Ebooks Élite en tu
            clase.
            <br data-id="element-202" />
            <br data-id="element-203" />
            Si no es exactamente lo que esperabas →{' '}
            <strong data-id="element-204">100% de devolución.</strong>
            <br data-id="element-205" />
            <br data-id="element-206" />
            Sin preguntas. Sin formularios. Sin drama.
          </p>
          <div className="inline-block bg-gray-100 px-4 py-2 rounded-full text-sm font-bold text-gray-500" data-id="element-207">
            TASA DE DEVOLUCIONES MENOR AL 2%
          </div>
        </div>
      </section>

      {/* 🔴 SECCIÓN 12 — FAQ */}
      <section className="py-20 bg-white px-4" data-id="element-208">
        <div className="max-w-3xl mx-auto" data-id="element-209">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12" data-id="element-210">
            Preguntas Frecuentes
          </h2>

          <div className="space-y-6" data-id="element-211">
            {[{
            q: '¿Cómo recibo el material?',
            a: 'Al confirmar el pago recibís un email con el enlace de descarga. Acceso inmediato, disponible las 24 horas, desde cualquier dispositivo.'
          }, {
            q: '¿Sirve si soy profe nuevo o si tengo muchos años de experiencia?',
            a: 'Sí para ambos. Si sos nuevo, te da la estructura que necesitás para empezar con seguridad. Si tenés experiencia, te ahorra las horas de planificación que ya no querés seguir regalando.'
          }, {
            q: '¿Los 6 Bonus están en las dos opciones?',
            a: 'Sí. Los 6 Bonus están incluidos en el Pack Profesional ($18.990) y en el Megapack Élite ($27.990). Los 2 Ebooks Élite son exclusivos del Megapack.'
          }, {
            q: '¿Qué diferencia hay con los materiales que ya se consiguen gratis en internet?',
            a: 'Los materiales de internet son genéricos, de otros países o no están adaptados al diseño curricular argentino. Biología 360 está diseñado específicamente para el sistema educativo argentino, en A4 para fotocopiadora, con actividades graduadas por nivel y año.'
          }, {
            q: '¿Sirve si tengo alumnos de distintos años en la misma escuela?',
            a: 'Sí. El sistema cubre 1° a 6° año con tomos independientes. Podés usarlo en todos tus cursos simultáneamente.'
          }, {
            q: '¿Qué pasa si no me sirve?',
            a: 'Tenés 7 días para revisarlo todo. Si no es lo que esperabas, te devolvemos el 100% del dinero sin preguntas.'
          }, {
            q: '¿Vale la pena el Megapack Élite si ya planifico bien mis clases?',
            a: 'Sí. El Élite 1 (Banco de TPs) te ahorra la parte más tediosa de la planificación: inventar consignas cada semana. El Élite 2 (ESI + EAI) resuelve la parte más exigida por los directivos y supervisores hoy en Argentina. Son 40 horas de trabajo ahorradas por $9.000 más.'
          }, {
            q: '¿Puedo pagar con tarjeta de crédito?',
            a: 'Sí. Aceptamos todas las tarjetas de crédito y débito, Mercado Pago y transferencia bancaria.'
          }].map((faq, idx) => <div key={idx} className="bg-gray-50 p-6 rounded-xl border border-gray-200" data-id="element-212">
                <h3 className="font-bold text-lg text-gray-900 mb-2 flex items-start gap-2" data-id="element-213">
                  <span className="text-blue-600" data-id="element-214">❓</span> {faq.q}
                </h3>
                <p className="text-gray-600 pl-7" data-id="element-215">{faq.a}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 🔴 SECCIÓN 13 — CTA FINAL */}
      <section className="py-20 bg-gray-900 text-white px-4" data-id="element-216">
        <div className="max-w-4xl mx-auto text-center" data-id="element-217">
          <h2 className="text-3xl font-bold mb-6" data-id="element-218">
            🏆 BIOLOGÍA 360 — El sistema de planificación N°1 para profesores de
            Biología argentinos.
          </h2>
          <p className="text-2xl text-gray-400 italic mb-12" data-id="element-219">
            "Porque preparar una buena clase no debería costarte todas las
            noches de la semana."
          </p>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-12" data-id="element-220">
            {/* Pack Profesional */}
            <div className="bg-gray-800 rounded-2xl border border-gray-700 p-8 flex flex-col" data-id="element-221">
              <div className="mb-6" data-id="element-222">
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-223">
                  📘 PACK PROFESIONAL
                </h3>
                <p className="text-gray-400" data-id="element-224">12 Tomos + 6 Bonus</p>
              </div>
              <div className="mb-8" data-id="element-225">
                <p className="text-gray-500 line-through text-lg" data-id="element-226">$29.990</p>
                <p className="text-4xl font-extrabold text-white" data-id="element-227">$18.990</p>
              </div>
              <button onClick={() => goToCheckout('profesional')} className="mt-auto w-full bg-gray-700 hover:bg-gray-600 text-white font-bold py-4 px-6 rounded-xl transition-colors flex justify-center items-center gap-2" data-id="element-228">
                QUIERO MI PACK PROFESIONAL <ChevronRight size={20} data-id="element-229" />
              </button>
            </div>

            {/* Megapack Élite */}
            <div className="bg-blue-900 rounded-2xl border-2 border-blue-500 p-8 flex flex-col relative shadow-2xl" data-id="element-230">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-yellow-400 text-yellow-900 font-bold px-4 py-1 rounded-full text-sm flex items-center gap-1" data-id="element-231">
                <Star size={14} className="fill-current" data-id="element-232" /> MÁS ELEGIDO
              </div>
              <div className="mb-6 mt-2" data-id="element-233">
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-234">
                  🏆 MEGAPACK ÉLITE
                </h3>
                <p className="text-blue-300" data-id="element-235">
                  12 Tomos + 6 Bonus + 2 Ebooks Élite
                </p>
              </div>
              <div className="mb-8" data-id="element-236">
                <p className="text-blue-400 line-through text-lg" data-id="element-237">$49.990</p>
                <p className="text-4xl font-extrabold text-white" data-id="element-238">$27.990</p>
              </div>
              <button onClick={() => goToCheckout('elite')} className="mt-auto w-full bg-green-500 hover:bg-green-600 text-white font-bold py-4 px-6 rounded-xl shadow-[0_4px_0_rgb(21,128,61)] hover:shadow-[0_2px_0_rgb(21,128,61)] hover:translate-y-[2px] transition-all flex justify-center items-center gap-2 text-lg" data-id="element-239">
                SÍ, QUIERO MI MEGAPACK ÉLITE <ChevronRight size={20} data-id="element-240" />
              </button>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400 mb-16" data-id="element-241">
            <span className="flex items-center gap-2" data-id="element-242">
              <Lock size={16} data-id="element-243" /> Pago seguro
            </span>
            <span className="flex items-center gap-2" data-id="element-244">
              <Clock size={16} data-id="element-245" /> Acceso en 5 minutos
            </span>
            <span className="flex items-center gap-2" data-id="element-246">
              <ShieldCheck size={16} data-id="element-247" /> Garantía 7 días
            </span>
            <span className="flex items-center gap-2" data-id="element-248">
              <CreditCard size={16} data-id="element-249" /> Tarjeta · Transferencia · Mercado Pago
            </span>
          </div>

          <footer className="border-t border-gray-800 pt-8 text-sm text-gray-500 flex flex-col items-center gap-4" data-id="element-250">
            <div className="flex flex-wrap justify-center gap-4 md:gap-8" data-id="element-251">
              <a href="#" className="hover:text-gray-300 transition-colors" data-id="element-252">
                📧 contacto@biologia360.com.ar
              </a>
              <span className="hidden md:inline" data-id="element-253">|</span>
              <a href="#" className="hover:text-gray-300 transition-colors" data-id="element-254">
                📲 @biologia360.ar
              </a>
              <span className="hidden md:inline" data-id="element-255">|</span>
              <a href="#" className="hover:text-gray-300 transition-colors" data-id="element-256">
                🌐 www.biologia360.ar
              </a>
            </div>
            <p className="mt-4 text-center" data-id="element-257">
              © BIOLOGÍA 360™ 2025 — Todos los derechos reservados.
              <br data-id="element-258" />
              Prohibida la reproducción y redistribución sin autorización
              expresa.
            </p>
          </footer>
        </div>
      </section>
    </div>;
}